import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { GraduationCap, ClipboardList, MessageSquare, Search } from "lucide-react";

const TABS = [
    { key: "coach", label: "My Partner Coach", icon: GraduationCap },
    { key: "suggestions", label: "Suggestions", icon: ClipboardList },
    { key: "feedback", label: "Feedback", icon: MessageSquare },
    { key: "find", label: "Find a Partner Coach", icon: Search },
];

/**
 * Coaching section tab bar. Rendered on top for desktop (in MyCoaching) and
 * below each tab's page header on mobile. Reads/writes the ?tab query param.
 */
export default function CoachingTabs({ className = "" }) {
    const { isDarkMode } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get("tab") || "coach";
    const activeKey = useMemo(
        () => TABS.find((tab) => tab.key === requestedTab)?.key || "coach",
        [requestedTab]
    );

    const handleTabChange = (tabKey) => {
        const nextParams = new URLSearchParams(searchParams);
        if (tabKey === "coach") nextParams.delete("tab");
        else nextParams.set("tab", tabKey);
        setSearchParams(nextParams, { replace: true });
    };

    return (
        <div className={className}>
            <div
                className={`rounded-2xl border p-1 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}
                role="tablist"
                aria-label="Partner Coaching sections"
            >
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeKey === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => handleTabChange(tab.key)}
                                className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                                    isActive
                                        ? isDarkMode
                                            ? "bg-slate-700 text-white"
                                            : "bg-primary-50 text-primary-700"
                                        : isDarkMode
                                            ? "text-slate-300 hover:bg-slate-700/60"
                                            : "text-slate-600 hover:bg-slate-50"
                                }`}
                            >
                                <Icon size={16} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
